<template>
    <MainContent
        title="Listado de precios"
        v-model="filters"
        :meta="meta"
        :loading="!hasLoadedOnce && loading"
    >
        <template #header>
            <template v-if="!hasLoadedOnce && loading">
                <Card customClasses="tw:mb-4">
                    <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                        <template #skeleton>
                            <div class="tw:flex tw:flex-col tw:gap-4">
                                <SkeletonItem height="tw:h-5" customClass="tw:w-64 tw:rounded" />
                                <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                            </div>
                        </template>
                    </Skeleton>
                </Card>
            </template>
            <template v-else>
                <Card customClasses="tw:mb-4">
                    <div class="tw:flex tw:flex-col tw:gap-4">
                        <h3 class="tw:text-lg tw:font-semibold">Listado de precios con su resumen</h3>
                        <div class="tw:flex tw:flex-wrap tw:gap-4 tw:items-center">
                            <SearchComponent
                                :value="filters.search"
                                :disabled="loading"
                                @input="val => { filters.search = val }"
                            />
                        </div>
                    </div>
                </Card>
            </template>
        </template>

        <BaseTable
            :columns="columns"
            :data="categorias.data || []"
            :skeleton="loading"
            :skeleton-rows="5"
            empty-text="No hay categorías"
            :default-col-width="defaultColWidth"
        >
            <!-- Skeleton headers -->
            <template #skeleton-header-id>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-8"></div>
            </template>
            <template #skeleton-header-name>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-header-precio>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-header-total_deportistas>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-28"></div>
            </template>
            <template #skeleton-header-media_edad>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-header-opciones>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-28"></div>
            </template>

            <!-- Skeleton rows -->
            <template #skeleton-id>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-10"></div>
            </template>
            <template #skeleton-name>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-24"></div>
            </template>
            <template #skeleton-precio>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-16"></div>
            </template>
            <template #skeleton-total_deportistas>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-32"></div>
            </template>
            <template #skeleton-media_edad>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-opciones>
                <div class="tw:h-9 tw:w-28 tw:bg-gray-200 tw:rounded"></div>
            </template>

            <template #cell-precio="{ row }">
                {{ formatPrice(row.precio) }}
            </template>
            <template #cell-opciones="{ row }">
                <OptionsDropdownComponent
                    :options="[
                        { to: { name: 'back.bases.categorias.resumen', params: { lista_id: props.lista_id, categoria_id: row.id } }, label: 'Resumen' }
                    ]"
                />
            </template>
        </BaseTable>
    </MainContent>
</template>

<script setup>
    import { ref, watch } from 'vue';

    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';

    import { useCategoriasInforme } from '@/composables/admin/bases/informe/categorias/useCategoriasInforme';
    import appFilters from '@/filters';

    const props = defineProps({
        lista_id: { type: [Number, String], required: true },
        defaultColWidth: {
            type: String,
            default: '120px'
        }
    });

    const {
        categorias,
        loading,
        filters,
        meta,
    } = useCategoriasInforme();

    const columns = [
        { key: 'id', label: 'ID', class: 'tw:w-16', centered:true },
        { key: 'name', label: 'Nombre', centered:true },
        { key: 'precio', label: 'Precio', class: 'tw:w-24', centered:true },
        { key: 'total_deportistas', label: 'Total de deportistas', centered:true },
        { key: 'media_edad', label: 'Media de edad', centered:true },
        { key: 'opciones', label: 'Acciones', class: 'tw:w-32', centered:true }
    ];

    const hasLoadedOnce = ref(false);
    watch(categorias, val => {
        if (val?.data?.length) hasLoadedOnce.value = true;
    });

    const formatPrice = appFilters.formatear_precio;
</script>
